import { faRotateRight } from "@fortawesome/pro-solid-svg-icons"
import { Typography } from "@mui/material"
import { translate } from "app/language/service"
import { getStore } from "app/store/store"
import { getActiveAppContext, wait } from "app/tools/common"
import { CAMPAIGN_MERGE_ERROR_CONFLICT_KEY, CAMPAIGN_STATUSES, ICampaign } from "campaign/model"
import { campaignRPO } from "campaign/repository"
import { hasCampaignChangedOnServer } from "campaign/tools/common"
import { ChangeDetails } from "form/components/layout/ChangeDetails"
import { formEventModule } from "form/event"
import { FORM_EVENTS, FORM_TABS, IForm } from "form/model"
import { state } from "form/state"
import { formActionCreator } from "form/store/actions"
import { getFormData } from "form/store/state"
import { messageSVC } from "message/service"
import { CAMPAIGN_SUMMARY_PATH, DSP_DEAL_SUMMARY_PATH, FORECAST_SUMMARY_PATH } from "page/model"
import { navigate } from "page/tools/navigate"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { synchronizeAuctions } from "./auction"
import { synchronizeCaps } from "./cap"
import { getFormChangesCount } from "./change"
import { getForm, isCreateForm } from "./common"
import {
  getConfigurationChanges,
  getConfigurationUpdateParams,
  getModifiedDataSinceLastUpdate,
  processCreateOrUpdateCampaign,
  updateConfigurationRelations
} from "./configuration"
import { loadCampaignForEdit } from "./edit"
import { synchronizeSchedule, synchronizeWeather } from "./schedule"
import { synchronizeUserTags } from "./tags"

export function cancelForm(): void {
  const form = getForm()
  const isForecastForm = form.context.isForecast
  const isDspDealForm = form.context.isDspDeal
  const processCancel = () => {
    navigate({
      path: isForecastForm ? FORECAST_SUMMARY_PATH : isDspDealForm ? DSP_DEAL_SUMMARY_PATH : CAMPAIGN_SUMMARY_PATH,
      pathParams: form.campaignId,
      ignoreLeavingMessage: true
    })
    getStore().dispatch(formActionCreator.clearAppForm(form.context.appContext))
  }
  if (getFormChangesCount(form.changes) === 0) {
    processCancel()
    return
  }
  messageSVC.confirmMessage({
    title: translate(
      isForecastForm
        ? "forecast.update.cancel.title"
        : isDspDealForm
          ? "dsp-deal.update.cancel.title"
          : "campaign.update.cancel.title"
    ),
    addGenericConfirmMessage: true,
    message: translate(
      isForecastForm
        ? "forecast.update.cancel.message"
        : isDspDealForm
          ? "dsp-deal.update.cancel.message"
          : "campaign.update.cancel.message"
    ),
    onSuccess: () => processCancel()
  })
}

export function saveForm(): void {
  const form = getForm()
  const isForecastForm = form.context.isForecast
  const isDspDealForm = form.context.isDspDeal
  const isCreation = isCreateForm(form)
  messageSVC.confirmMessage({
    icon: faRotateRight,
    confirmButtonLabel: isCreation ? translate("button.create") : translate("label.update"),
    title: translate(
      `${isForecastForm ? "forecast" : isDspDealForm ? "dsp-deal" : "campaign"}.${isCreation ? "create" : "update"}.title`
    ),
    message: [
      translate(
        `${isForecastForm ? "forecast" : isDspDealForm ? "dsp-deal" : "campaign"}.${isCreation ? "create.confirm" : "update.resume"}.message`,
        form.configuration.main.name
      )
    ],
    children: isCreation ? undefined : (
      <FlexBox vertical ai="start" jc="start">
        <ChangeDetails />
        <Typography fontSize=".9rem" color="textPrimary" sx={{ mt: "1rem" }}>
          {translate(
            isForecastForm
              ? "forecast.update.confirm.message"
              : isDspDealForm
                ? "dsp-deal.update.confirm.message"
                : "campaign.update.confirm.message",
            form.configuration.main.name
          )}
        </Typography>
        <Typography fontSize=".9rem" color="textPrimary">
          {translate("label.continue.confirmation")}
        </Typography>
      </FlexBox>
    ),
    onSuccess: async () => {
      try {
        await processSaveForm()
        navigate({
          path: isForecastForm ? FORECAST_SUMMARY_PATH : isDspDealForm ? DSP_DEAL_SUMMARY_PATH : CAMPAIGN_SUMMARY_PATH,
          pathParams: isCreation ? form.tempCampaignId : form.campaignId,
          ignoreLeavingMessage: true
        })
        getStore().dispatch(formActionCreator.clearAppForm(form.context.appContext, isCreation))
      } catch (error) {
        console.error(error)
        messageSVC.error(
          translate(
            `${isForecastForm ? "forecast" : isDspDealForm ? "dsp-deal" : "campaign"}.${isCreation ? "create" : "update"}.error`
          )
        )
      }
    }
  })
}

export function clearForm(): void {
  messageSVC.confirmMessage({
    title: translate("campaign.clear.confirm.message.title"),
    addGenericConfirmMessage: true,
    message: translate("campaign.clear.confirm.message"),
    onSuccess: () => {
      const form = getForm()
      getStore().dispatch(
        formActionCreator.clearAppForm(form.context.appContext, form.activeTab === FORM_TABS.CONFIGURATION)
      )
      formEventModule.notify(FORM_EVENTS.FORM_CLEARED, form)
    }
  })
}

export function stopRefreshingFormikForm(): void {
  getStore().dispatch(formActionCreator.setRefreshFormikForm(getForm().context.appContext, false))
}

function processSaveForm(): Promise<void> {
  return new Promise<void>(async (resolve, reject) => {
    try {
      let form = getForm()
      if (form.context.isSavingTempCampaign) {
        while (form.context.isSavingTempCampaign) {
          await wait(300)
          form = getForm()
        }
      } else if (state.pendingFormToSave) {
        // This case can happened in fast automatic tests
        const pendingForm = state.pendingFormToSave
        state.pendingFormToSave = null
        await processCreateOrUpdateCampaign(pendingForm)
      }
      getStore().dispatch(formActionCreator.setIsSavingCampaign(form.context.appContext, true))
      await checkRelationsSynchronizationErrors()
      const isForecastForm = form.context.isForecast
      const isDspDealForm = form.context.isDspDeal
      const isCreation = isCreateForm(form)
      if (isCreation) {
        await campaignRPO.update({
          id: form.tempCampaignId,
          status_id: (isForecastForm
            ? CAMPAIGN_STATUSES.PLANNING
            : isDspDealForm
              ? CAMPAIGN_STATUSES.ACTIVE
              : CAMPAIGN_STATUSES.PENDING) as any
        })
        getStore().dispatch(formActionCreator.setIsSavingCampaign(form.context.appContext, false))
        // no refresh formik because we'll navigate to summary page
        getStore().dispatch(formActionCreator.clearAppForm(form.context.appContext, true))
        resolve()
        return
      }
      // it's an update
      const processUpdate = async () => {
        if (await processEditCampaign(form)) {
          resolve()
        } else {
          reject("unable to update the campaign")
        }
        getStore().dispatch(formActionCreator.setIsSavingCampaign(form.context.appContext, false))
      }
      const initialCampaign: ICampaign = form.context.originalCampaign as ICampaign
      const campaignHasChangedServerSide = await hasCampaignChangedOnServer(initialCampaign)
      if (!campaignHasChangedServerSide) {
        processUpdate()
        return
      }
      // display message and wait for choice
      messageSVC.confirmMessage({
        title: translate("campaign.merge.error.conflict.title"),
        message: `${translate(CAMPAIGN_MERGE_ERROR_CONFLICT_KEY)}. ${translate(
          "campaign.merge.error.conflict.choice"
        )}`,
        isWarning: true,
        confirmButtonLabel: translate("button.continue"),
        onSuccess: async () => processUpdate(), // overwrite server side changes
        onCancel: async () => {
          // reload the form with server side changes
          getStore().dispatch(formActionCreator.setIsSavingCampaign(form.context.appContext, false))
          await loadCampaignForEdit(initialCampaign.id, initialCampaign.status_id, isForecastForm, isDspDealForm)
          resolve()
        }
      })
    } catch (error: any) {
      console.error(error)
      reject("Unexpected error while saving the form")
      if (getActiveAppContext() !== null) {
        getStore().dispatch(formActionCreator.setIsSavingCampaign(getForm().context.appContext, false))
      }
      messageSVC.error(translate("campaign.save.error.unknown"), { duration: 10000 })
    }
  })
}

async function processEditCampaign(form: IForm): Promise<boolean> {
  try {
    await campaignRPO.merge(form.tempCampaignId, form.campaignId)
    return true
  } catch (errorKey: any) {
    messageSVC.error(translate(errorKey), { duration: 10000 })
    console.error("Unexpected error while merging the campaign")
    return false
  }
}

// some errors could have happened when synchronising the data
// we'll try to synchronize a last time, and reject if it doesn't works
async function checkRelationsSynchronizationErrors(): Promise<void> {
  let form = getForm()
  if (form.context.saveError) {
    // means
    const modifiedData = getModifiedDataSinceLastUpdate(form)
    await campaignRPO.update(getConfigurationUpdateParams(form, modifiedData))
    await updateConfigurationRelations(form, modifiedData)
    getStore().dispatch(
      formActionCreator.stopSavingTempCampaign(
        form.context.appContext,
        getConfigurationChanges(form),
        getFormData(form)
      )
    )
    form = getForm()
    if (form.context.saveError) {
      throw Error("Unable to save the campaign")
    }
  }
  let relations = form.context.relations
  // CAPS
  if (relations.capsError) {
    await synchronizeCaps(form)
    relations = getForm().context.relations
    if (relations.capsError) {
      throw Error("Unable to save caps")
    }
  }
  // WEATHER
  if (relations.weathersError) {
    await synchronizeWeather(form)
    relations = getForm().context.relations
    if (relations.weathersError) {
      throw Error("Unable to save weathers")
    }
  }
  // AUCTIONS
  if (relations.auctionsError) {
    await synchronizeAuctions(form)
    relations = getForm().context.relations
    if (relations.auctionsError) {
      throw Error("Unable to save auctions")
    }
  }
  // SCHEDULE
  if (relations.scheduleError) {
    await synchronizeSchedule(form)
    relations = getForm().context.relations
    if (relations.scheduleError) {
      throw Error("Unable to save schedule")
    }
  }
  // USER TAGS
  if (relations.userTagsError) {
    await synchronizeUserTags(form)
    relations = getForm().context.relations
    if (relations.userTagsError) {
      throw Error("Unable to save user tags")
    }
  }
}
